<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { gameStore } from '../../stores/gameStore';
  import { lobbyStore } from '../../stores/lobbyStore';
  import { presentationStore, formattedTime } from '../../stores/presentationStore';
  import { announceToScreenReader } from '../../lib/accessibility';

  $: gameState = $gameStore.gameState;
  $: scores = gameState?.scores || [];
  $: currentRound = gameState?.currentRound || 0;
  $: sortedScores = [...scores].sort((a, b) => b.score - a.score);

  let timerInterval: ReturnType<typeof setInterval> | null = null;

  // Update timer countdown every 100ms
  onMount(() => {
    timerInterval = setInterval(() => {
      if (gameState?.timerEndTime) {
        const remainingTime = Math.max(0, gameState.timerEndTime - Date.now());
        presentationStore.updateRemainingTime(remainingTime);
      }
    }, 100);
  });

  onDestroy(() => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
  });

  // Announce round completion
  $: if (gameState?.phase === 'round_end') {
    const topPlayer = sortedScores[0];
    if (topPlayer) {
      announceToScreenReader(
        `Round ${currentRound} complete. ${topPlayer.username} is in the lead with ${topPlayer.score} points.`,
        'assertive'
      );
    }
  }
</script>

<div class="round-end">
  <div class="round-end-card">
    <h2>Round {currentRound} Complete!</h2>

    <div class="scoreboard">
      <h3>Current Standings</h3>
      <table>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Player</th>
            <th>Score</th>
          </tr>
        </thead>
        <tbody>
          {#each sortedScores as player, index (player.playerId)}
            <tr class:leader={index === 0}>
              <td class="rank">#{index + 1}</td>
              <td class="player">{player.username}</td>
              <td class="score">{player.score}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="timer" role="timer">
      Next round starts in {$formattedTime}
    </div>
  </div>
</div>

<style>
  .round-end {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 2rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .round-end-card {
    background: white;
    border-radius: 1rem;
    padding: 3rem;
    max-width: 800px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  h2 {
    margin: 0 0 2rem 0;
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
  }

  .scoreboard {
    margin-bottom: 2rem;
  }

  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    text-align: left;
    padding: 0.75rem;
    background: #f3f4f6;
    font-weight: 600;
    color: #4b5563;
    border-bottom: 2px solid #e5e7eb;
  }

  td {
    padding: 1rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  tr.leader {
    background: #fef3c7;
    font-weight: 600;
  }

  .rank {
    color: #6b7280;
    font-weight: 700;
  }

  .score {
    font-size: 1.25rem;
    font-weight: 700;
    color: #3b82f6;
  }

  .timer {
    text-align: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: #7c3aed;
    padding: 1rem;
    background: #f3f4f6;
    border-radius: 0.5rem;
  }
</style>
